import { RequestKind, boolean, string } from '@sb/library/base';
import { AdditionalStudentSignupInputItem, ContactFormInputItem, DisplayItemCollection, SignupInputItem, SignupResultItem, VerifyUrlInputItem, WebsiteBlockAnyDisplayItem, WebsiteBlockDisplayItem, WebsiteBlockInputItem, WebsiteBlockSearchOptions, WebsiteBlockUpdateItem, WebsiteCustomFieldDisplayItem, WebsiteCustomFieldInputItem, WebsiteCustomFieldSearchOptions, WebsiteCustomFieldUpdateItem, WebsiteDisplayItem, WebsiteInputItem, WebsitePageDisplayItem, WebsitePageInputItem, WebsitePageSearchOptions, WebsitePageUpdateItem, WebsiteURLValidationResult, WebsiteUpdateItem } from '@sb/library/data-models';
import { ApiRoute } from '../api-route';

export namespace WebsiteRoutes {
    export const GetBySchool = ApiRoute.Define({
        URL: (schoolID: string, websiteID: string) => `schools/${schoolID}/websites/${websiteID}`,
        Kind: RequestKind.Get,
        ResultType: WebsiteDisplayItem
    });

    export const Get = ApiRoute.Define({
        URL: (websiteID: string) => `websites/${websiteID}`,
        Kind: RequestKind.Get,
        ResultType: WebsiteDisplayItem
    });

    export const GetAll = ApiRoute.Define({
        URL: `websites`,
        Kind: RequestKind.Get,
        ResultType: DisplayItemCollection<WebsiteDisplayItem>
    });

    export const VerifyURL = ApiRoute.Define({
        URL: `websites/verifyurl`,
        Kind: RequestKind.Post,
        BodyType: VerifyUrlInputItem,
        ResultType: WebsiteURLValidationResult
    });

    export const GetRandomUnusedHostname = ApiRoute.Define({
        URL: `websites/unusedhostname`,
        Kind: RequestKind.Get,
        ResultType: string
    });

    export const Update = ApiRoute.Define({
        URL: (websiteID: string) => `websites/${websiteID}`,
        Kind: RequestKind.Put,
        BodyType: WebsiteUpdateItem,
        ResultType: boolean
    });

    export const Create = ApiRoute.Define({
        URL: `websites`,
        Kind: RequestKind.Post,
        BodyType: WebsiteInputItem,
        ResultType: string
    });

    export const Delete = ApiRoute.Define({
        URL: (websiteID: string) => `websites/${websiteID}`,
        Kind: RequestKind.Delete,
        ResultType: boolean
    });

    export namespace Page {
        export const Get = ApiRoute.Define({
            URL: (websitePageID: string) => `websitepages/${websitePageID}`,
            Kind: RequestKind.Get,
            ResultType: WebsitePageDisplayItem
        });

        export const GetAll = ApiRoute.Define({
            URL: `websitepages`,
            Kind: RequestKind.Get,
            ResultType: DisplayItemCollection<WebsitePageDisplayItem>
        });

        export const Search = ApiRoute.Define({
            URL: `search/websitepages`,
            Kind: RequestKind.Post,
            BodyType: WebsitePageSearchOptions,
            PagingAllowed: true,
            ResultType: DisplayItemCollection<WebsitePageDisplayItem>
        });

        export const Add = ApiRoute.Define({
            URL: (websiteID: string) => `websites/${websiteID}/websitepages`,
            Kind: RequestKind.Post,
            BodyType: WebsitePageInputItem,
            ResultType: string
        });

        export const AddChild = ApiRoute.Define({
            URL: (websitePageID: string) => `websitepages/${websitePageID}/websitepages`,
            Kind: RequestKind.Post,
            BodyType: WebsitePageInputItem,
            ResultType: string
        });

        export const Update = ApiRoute.Define({
            URL: (websitePageID: string) => `websitepages/${websitePageID}`,
            Kind: RequestKind.Put,
            BodyType: WebsitePageUpdateItem,
            ResultType: boolean
        });

        export const Delete = ApiRoute.Define({
            URL: (websitePageID: string) => `websitepages/${websitePageID}`,
            Kind: RequestKind.Delete,
            ResultType: boolean
        });
    }

    export namespace Block {
        export const GetBySchool = ApiRoute.Define({
            URL: (schoolID: string, websiteBlockID: string) => `schools/${schoolID}/websiteblocks/${websiteBlockID}`,
            Kind: RequestKind.Get,
            ResultType: WebsiteBlockDisplayItem
        });

        export const Get = ApiRoute.Define({
            URL: (websiteBlockID: string) => `websiteblocks/${websiteBlockID}`,
            Kind: RequestKind.Get,
            ResultType: WebsiteBlockDisplayItem,
            RequestedFieldsType: WebsiteBlockAnyDisplayItem
        });

        export const GetAll = ApiRoute.Define({
            URL: `websiteblocks`,
            Kind: RequestKind.Get,
            ResultType: DisplayItemCollection<WebsiteBlockDisplayItem>
        });

        export const Search = ApiRoute.Define({
            URL: `search/websiteblocks`,
            Kind: RequestKind.Post,
            BodyType: WebsiteBlockSearchOptions,
            PagingAllowed: true,
            RequestedFieldsType: WebsiteBlockAnyDisplayItem,
            ResultType: DisplayItemCollection<WebsiteBlockDisplayItem>
        });

        export const Add = ApiRoute.Define({
            URL: (websiteid: string) => (websiteid.startsWith('wbs') ? 'websites' : 'websitepages') + `/${websiteid}/websiteblocks`,
            Kind: RequestKind.Post,
            BodyType: WebsiteBlockInputItem,
            ResultType: string
        });

        export const Update = ApiRoute.Define({
            URL: (websiteBlockID: string) => `websiteblocks/${websiteBlockID}`,
            Kind: RequestKind.Put,
            BodyType: WebsiteBlockUpdateItem,
            ResultType: boolean
        });

        export const Delete = ApiRoute.Define({
            URL: (websiteBlockID: string) => `websiteblocks/${websiteBlockID}`,
            Kind: RequestKind.Delete,
            ResultType: boolean
        });
    }

    export namespace CustomField {
        export const Get = ApiRoute.Define({
            URL: (customFieldID: string) => `websitecustomfields/${customFieldID}`,
            Kind: RequestKind.Get,
            ResultType: WebsiteCustomFieldDisplayItem
        });

        export const GetAll = ApiRoute.Define({
            URL: `websitecustomfields`,
            Kind: RequestKind.Get,
            ResultType: DisplayItemCollection<WebsiteCustomFieldDisplayItem>
        });

        export const Search = ApiRoute.Define({
            URL: `search/websitecustomfields`,
            Kind: RequestKind.Post,
            BodyType: WebsiteCustomFieldSearchOptions,
            ResultType: DisplayItemCollection<WebsiteCustomFieldDisplayItem>
        });

        export const Add = ApiRoute.Define({
            URL: (websiteBlockID: string) => `websiteblocks/${websiteBlockID}/websitecustomfields`,
            Kind: RequestKind.Post,
            BodyType: WebsiteCustomFieldInputItem,
            ResultType: string
        });

        export const Update = ApiRoute.Define({
            URL: (customFieldID: string) => `websitecustomfields/${customFieldID}`,
            Kind: RequestKind.Put,
            BodyType: WebsiteCustomFieldUpdateItem,
            ResultType: boolean
        });

        export const Delete = ApiRoute.Define({
            URL: (customFieldID: string) => `websitecustomfields/${customFieldID}`,
            Kind: RequestKind.Delete,
            ResultType: boolean
        });
    }

    export namespace Signup {
        export const Initial = ApiRoute.Define({
            URL: `signup`,
            Kind: RequestKind.Post,
            BodyType: SignupInputItem,
            ExtraParameters: 'TimeZone',
            ResultType: SignupResultItem
        });

        export const Additional = ApiRoute.Define({
            URL: 'signup',
            Kind: RequestKind.Put,
            BodyType: AdditionalStudentSignupInputItem,
            ExtraParameters: 'TimeZone',
            ResultType: SignupResultItem
        });
    }

    export const Contact = ApiRoute.Define({
        URL: `contact`,
        Kind: RequestKind.Post,
        BodyType: ContactFormInputItem,
        ResultType: boolean
    });
}
